import { useMemo } from "react";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import { FormPropertyBox } from "@foodpilot/foods";
import { t } from "i18next";

import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { useGetOrganizationPost } from "src/api/client-api/post/useGetOrganizationPost";
import EmptyStateCard from "src/components/EmptyState/EmptyStateCard";
import { useGetOneSsq } from "src/api/sure-api/ssq/useGetOneSsq";
import { SsqSsqHeading, SsqSsqHeadingSsqProperty, useGetOneSsqVersion } from "src/api/sure-api/ssq/useGetOneSsqVersion";

export type SsqAnswersFieldConfig = {
    ssqPropertyId: number;
    title?: string;
    hint?: string;
};

export type SsqAnswersProps = {
    title?: string;
    fields?: SsqAnswersFieldConfig[];
};

const isEmptyHtml = (value: string | undefined): boolean => {
    if (!value) return true;
    const v = value.trim();
    return v === "" || v === "<p></p>" || v === "<p><br></p>";
};

const stripHtml = (value: string): string => {
    return value
        .replace(/<[^>]*>/g, " ")
        .replace(/\s+/g, " ")
        .trim();
};

export const SsqAnswers = (props: SsqAnswersProps) => {
    const theme = useTheme();
    const { title, fields = [] } = props;

    const { selectedCompany, currentCampaign } = useFoodpilotContext();

    const companyId = selectedCompany?.id ? Number(selectedCompany.id) : undefined;
    const campaignId = currentCampaign?.id ? Number(currentCampaign.id) : undefined;
    const ssqId = selectedCompany?.organizationSector?.primarySsqId || selectedCompany?.organizationSector?.ssqId;

    const { post: companyPost, isLoading } = useGetOrganizationPost({
        companyId,
        ssqId,
        campaignId,
        enabled: Boolean(companyId && ssqId && campaignId),
    });
    const { ssq } = useGetOneSsq(ssqId);
    const { ssqVersion } = useGetOneSsqVersion(ssq?.currentVersion.id);

    const elementsByPropertyId = useMemo(() => {
        const map = new Map<number, { value?: string; name?: string }>();
        const els = companyPost?.elements ?? [];
        els.forEach((el) => {
            if (typeof el?.ssqPropertyId !== "number") return;
            if (!map.has(el.ssqPropertyId)) {
                map.set(el.ssqPropertyId, {
                    value: el.value,
                    name: el.name,
                });
            }
        });
        return map;
    }, [companyPost?.elements]);

    const mapSsqPropertyUnitById = (map: Map<number, string>, ssqSsqHeadingSsqProperty: SsqSsqHeadingSsqProperty) => {
        const ssqProperty = ssqSsqHeadingSsqProperty.ssqProperty;

        if (!ssqProperty.unit) return;

        map.set(ssqProperty.id, ssqProperty.unit.abbreviation);
    };

    const mapSsqSsqHeadingUnitsByPropertyId = (map: Map<number, string>, ssqSsqHeading: SsqSsqHeading) => {
        ssqSsqHeading.ssqSsqHeadingSsqProperties.forEach((ssqSsqHeadingSsqProperty) => {
            ssqSsqHeadingSsqProperty.children?.forEach((childSsqSsqHeadingSsqProperty) => {
                mapSsqPropertyUnitById(map, childSsqSsqHeadingSsqProperty);
            });

            mapSsqPropertyUnitById(map, ssqSsqHeadingSsqProperty);
        });
    };

    const unitsByPropertyId = useMemo(() => {
        const map = new Map<number, string>();
        const parentSsqSsqHeadings = ssqVersion?.parentSsqSsqHeadings ?? [];

        parentSsqSsqHeadings.forEach((ssqSsqHeading) => {
            ssqSsqHeading.children?.forEach((childSsqSsqHeading) => {
                mapSsqSsqHeadingUnitsByPropertyId(map, childSsqSsqHeading);
            });

            mapSsqSsqHeadingUnitsByPropertyId(map, ssqSsqHeading);
        });

        return map;
    }, [ssq]);

    const displayFields = useMemo(() => {
        return (fields ?? []).flatMap((f) => {
            const id = Number(f.ssqPropertyId);
            if (!Number.isFinite(id) || id <= 0) return [];
            return [{ ...f, ssqPropertyId: id }];
        });
    }, [fields]);

    const hasAnyValue = useMemo(() => {
        return displayFields.some((f) => {
            const el = elementsByPropertyId.get(f.ssqPropertyId);
            const v = el?.value;
            return typeof v === "string" && v.trim() !== "";
        });
    }, [displayFields, elementsByPropertyId]);

    if (!isLoading && (!displayFields.length || !hasAnyValue)) {
        return (
            <Box
                sx={{
                    width: "100%",
                    height: "300px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                }}
            >
                <EmptyStateCard
                    title={t("cms.ssq_answers.nodata.title", "Unable to display answers")}
                    description={t("cms.ssq_answers.nodata.description", "Data not available for this section")}
                />
            </Box>
        );
    }

    return (
        <Stack gap={theme.spacing(3)}>
            {!isEmptyHtml(title) && (
                <Box
                    sx={{
                        ...theme.typography.h2,
                        fontWeight: theme.typography.fontWeightBold,
                        overflowWrap: "anywhere",
                        whiteSpace: "break-spaces",
                        "& p": {
                            margin: 0,
                            ...theme.typography.h2,
                            fontWeight: theme.typography.fontWeightBold,
                        },
                    }}
                    dangerouslySetInnerHTML={{ __html: title ?? "" }}
                />
            )}

            {displayFields.map((field, idx) => {
                const el = elementsByPropertyId.get(field.ssqPropertyId);
                const computedTitle =
                    !isEmptyHtml(field.title) ?
                        (() => {
                            const text = stripHtml(field.title ?? "");
                            return text !== "" ? text : `#${field.ssqPropertyId}`;
                        })()
                    : el?.name && el.name.trim() !== "" ? el.name
                    : `#${field.ssqPropertyId}`;

                const value = typeof el?.value === "string" && el.value.trim() !== "" ? el.value : "-";
                const unit = unitsByPropertyId.get(field.ssqPropertyId) ?? "";

                const hint = !isEmptyHtml(field.hint) ? (field.hint ?? "") : undefined;

                return (
                    <FormPropertyBox
                        key={`${field.ssqPropertyId}-${idx}`}
                        title={computedTitle}
                        hint={hint}
                        sx={{
                            padding: theme.spacing(3),
                            "& .MuiTypography-h4": {
                                fontWeight: theme.typography.fontWeightBold,
                            },
                        }}
                    >
                        <Typography variant="body1" sx={{ color: theme.palette.text.primary }}>
                            {unit ? `${value} ${unit}` : value}
                        </Typography>
                    </FormPropertyBox>
                );
            })}
        </Stack>
    );
};

export default SsqAnswers;
